"use client";

import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from 'recharts';

const gdpData = [
  { year: '2015', gdp: 74 },
  { year: '2016', gdp: 88 },
  { year: '2017', gdp: 102 },
  { year: '2018', gdp: 118 },
  { year: '2019', gdp: 130 },
  { year: '2020', gdp: 128 },
  { year: '2021', gdp: 134 },
  { year: '2022', gdp: 166 },
  { year: '2023', gdp: 200 },
  { year: '2024', gdp: 240 },
  { year: '2025', gdp: 260 },
];

const growthData = [
  { year: 'FY21', telangana: 6.1, india: 4.2 },
  { year: 'FY22', telangana: 11.2, india: 8.7 },
  { year: 'FY23', telangana: 9.8, india: 7.2 },
  { year: 'FY24', telangana: 8.5, india: 7.6 },
  { year: 'FY25', telangana: 8.2, india: 6.8 },
];

const CustomTooltip = ({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ value: number; name?: string; color?: string }>;
  label?: string;
}) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-border rounded p-3 shadow-card text-xs font-mono">
        <p className="text-primary font-semibold mb-1">{label}</p>
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color }}>
            {entry.name || 'Value'}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function EconomyCharts() {
  return (
    <section className="section-padding bg-white border-b border-border">
      <div className="container-research">
        <span className="section-label">Trend Analysis</span>
        <h2 className="text-h2 font-heading font-semibold text-primary mb-8">
          Historical Trends
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* GDP Chart */}
          <div className="bg-white border border-border rounded p-5 shadow-card">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-primary">Telangana GSDP (USD Billion)</h3>
                <p className="text-xs text-muted font-mono">2015–2025 · Source: RBI / MoSPI</p>
              </div>
              <span className="source-tag">Annual</span>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={gdpData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis
                    dataKey="year"
                    tick={{ fontSize: 11, fontFamily: 'JetBrains Mono', fill: '#6B7280' }}
                    tickLine={false}
                    axisLine={{ stroke: '#E2E8F0' }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fontFamily: 'JetBrains Mono', fill: '#6B7280' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `$${v}B`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="gdp"
                    stroke="#1B6CA8"
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#1B6CA8', strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                    name="GSDP ($B)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Growth Rate Comparison */}
          <div className="bg-white border border-border rounded p-5 shadow-card">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-primary">GDP Growth Rate Comparison (%)</h3>
                <p className="text-xs text-muted font-mono">Telangana vs India · Source: MoSPI</p>
              </div>
              <span className="source-tag">Annual</span>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={growthData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis
                    dataKey="year"
                    tick={{ fontSize: 11, fontFamily: 'JetBrains Mono', fill: '#6B7280' }}
                    tickLine={false}
                    axisLine={{ stroke: '#E2E8F0' }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fontFamily: 'JetBrains Mono', fill: '#6B7280' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{ fontSize: '11px', fontFamily: 'JetBrains Mono', color: '#6B7280' }}
                  />
                  <Bar dataKey="telangana" name="Telangana" fill="#1B6CA8" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="india" name="India" fill="#E2E8F0" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <p className="text-xs text-muted font-mono mt-4">
          * All GDP figures in constant 2015 USD. Growth rates are real GDP growth. FY25 figures are advance estimates.
        </p>
      </div>
    </section>
  );
}